
import {React, useState, useContext, useEffect, useRef} from "react"
import {useStyle} from '../../context/styleContext/styleContext'
import ReactGA from 'react-ga4';
import { Paper } from '@mui/material';
import { Container, Typography, Divider, Box, List, ListItem, ListItemText } from "@mui/material";
import WikiCommonsImage from "../../components/WikiCommonsImage/WikiCommonsImage";


export default function CompressionLearning(){

  const { style, setStyle } = useStyle();
  const [width, setWidth] = useState(window.innerWidth)

function handleWindowSizeChange() {
    setWidth(window.innerWidth);
}
useEffect(() => {
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
        window.removeEventListener('resize', handleWindowSizeChange);
    }
}, []);

const isMobile = width <= 768;

useEffect(() => {
  document.title = 'Parish Audio | Cutting-Edge Audio Processing | Compression';
}, []);

ReactGA.send({
    hitType:"pageview",
    page:"learning/compression",
    title:"learning/compression",
  })

  const [height, setHeight] = useState(window.innerHeight)

  useEffect(() => {
    function handleResize() {      
      setWidth(window.innerWidth)
      setHeight(window.innerHeight)
  }
  
    window.addEventListener('resize', handleResize)
  })

const topContainer = useRef();
    useEffect(() => {
        // To make sure page starts from the top
        topContainer.current.scrollIntoView({ block: "end" });
    }, []);




    return(
        <><div style={{margin:'auto', width:'95%', maxWidth:'800px'}}>

        <h1 style={{marginTop:'25px'}} ref={topContainer} >Audio Compression</h1>
        <Divider className="mobile-card-divider" />
        <h2 style={{marginTop:'25px'}}>Introduction</h2>
<p>Audio compression, specifically dynamic range compression (DRC), is a crucial tool in audio engineering, used to manipulate the volume levels of an audio signal. By reducing the difference between the loudest and softest parts of a sound, compression helps maintain a more consistent volume, enhances clarity, and adds character to a mix. It is widely used in music production, broadcasting, live sound, and various other applications. This document explores the principles, types, controls, uses, and advanced applications of audio compression.</p>

<h2 style={{marginTop:'30px'}}>Understanding Dynamic Range Compression</h2>
<p>Dynamic range refers to the difference between the quietest and loudest parts of an audio signal. Compression reduces this range by attenuating loud sounds or amplifying soft sounds. The device or software that performs this function is called a compressor.</p>
<p>In practice, compression is used to:</p>
<ul class="a">
    <li style={{marginBottom:'5px'}}>Balance levels in recordings</li>
    <li style={{marginBottom:'5px'}}>Balance levels in recordings</li>
    <li style={{marginBottom:'5px'}}>Control transients (sudden spikes in volume)</li>
    <li style={{marginBottom:'5px'}}>Prevent distortion and clipping in a mix</li>
    <li style={{marginBottom:'5px'}}>Create special audio effects</li>
</ul>
<p>
Compression is commonly applied to vocals, drums, bass, guitars, and entire mixes to achieve a polished, professional sound.
</p>


<h2 style={{marginTop:'30px'}}>Types of Compression</h2>
<p>There are two main types of dynamic range compression:</p>
<ul class="a">
    <li><strong>Downward Compression:</strong> Reduces the volume of sounds above a certain threshold while leaving quieter sounds unchanged. This is the most common type of compression.</li>
    <li><strong>Upward Compression:</strong> Increases the volume of quiet sounds while leaving louder sounds unaffected. This helps make softer details more audible.</li>
</ul>

<h2 style={{marginTop:'30px'}}>Compressor Design and Operation</h2>
<p>A compressor works by analyzing an audio signal and applying gain reduction based on specific parameters. The two main designs are:</p>
<ul class="a">
    <li><strong>Feed-Forward Compression: </strong>The input signal is analyzed before gain reduction is applied.</li>
    <li><strong>Feedback Compression:</strong>The output signal is analyzed, affecting gain changes retroactively.</li>
</ul>
<p>Compression can be implemented using analog hardware (e.g., tube, optical, FET, VCA compressors) or digital software plugins in digital audio workstations (DAWs).</p>

<h2 style={{marginTop:'30px'}}>Key Compressor Parameters</h2>
<p>Compression effectiveness depends on adjusting various control parameters:</p>
<ul class="a">
    <li><strong>Threshold:</strong> Sets the level above (or below) which compression starts. A lower threshold results in more compression.</li>
    <li><strong>Ratio:</strong> Determines the degree of compression. A 4:1 ratio means that for every 4 dB above the threshold, the output only increases by 1 dB. Higher ratios (10:1 or more) are considered limiting, which prevents signals from exceeding a certain level.</li>
    <li><strong>Attack Time:</strong> Defines how quickly compression engages when the signal exceeds the threshold. Faster attack times control transients, while slower attack times preserve natural dynamics.</li>
    <li><strong>Release Time:</strong> Controls how quickly the compression stops once the signal falls below the threshold. Shorter release times can make compression more noticeable, while longer times ensure smoothness.</li>
    <li><strong>Knee:</strong> Determines how smoothly compression is applied. A hard knee engages suddenly, while a soft knee gradually increases gain reduction.</li>
    <li><strong>Make-Up Gain:</strong> Restores lost volume due to compression, making the overall level louder without increasing peak levels.</li>
    <li><strong>Peak vs. RMS Sensing:</strong>Peak compression reacts to instantaneous peaks, while RMS compression measures the average level, mimicking human loudness perception.</li>
</ul>
<h2 style={{marginTop:'30px'}}>Special Compression Techniques</h2>
<h3>Sidechain Compression</h3>
<p>Sidechain compression uses an external audio source to control gain reduction. A common example is ducking, where background music lowers when a voice-over is detected (e.g., in radio broadcasts). In electronic music, sidechaining is often applied to basslines triggered by the kick drum to prevent clashing and create a pulsating effect.</p>
<h3>Parallel Compression (New York Compression)</h3>
<p>Parallel compression blends a heavily compressed signal with the original, preserving natural dynamics while increasing perceived loudness. This technique is widely used in drum processing and vocals.</p>
<h3>Multiband Compression</h3>
<p>Instead of applying compression to the entire frequency range, multiband compression splits the signal into different frequency bands, allowing for independent compression settings. This is useful in mastering and broadcasting to maintain balance across the audio spectrum.</p>
<h3>Serial Compression</h3>
<p>This method involves chaining multiple compressors in sequence, each applying a moderate amount of compression. This allows for smoother and more transparent compression compared to a single aggressive compressor.</p>
<h2>Applications of Compression</h2>
<h3>Music Production</h3>
<p>Compression is vital in mixing and mastering. It helps ensure that instruments and vocals sit well in the mix and controls dynamic inconsistencies. Specific applications include:</p>

<ul class="a">
    <li><strong>Vocals:</strong>  Controls volume fluctuations and enhances clarity.</li>
    <li><strong>Drums:</strong> Adds punch and sustain.</li>
    <li><strong>Guitars/Bass:</strong> Maintains consistent volume and sustain.</li>
    <li><strong>Mastering:</strong>  Increases loudness while preserving dynamics.</li>
</ul>

<h3>Broadcasting and Public Spaces</h3>
<p>Broadcasters use compression to ensure consistent audio levels and to comply with peak volume regulations. Loudness normalization (e.g., EBU R128, ITU-R BS.1770) has been implemented to prevent drastic volume differences between programs and advertisements.</p>

<h3>Hearing Aids and Speech Processing</h3>
<p>Hearing aids use compression to amplify soft sounds while preventing loud noises from becoming overwhelming. Speech processors in mobile phones and communication devices apply compression to improve intelligibility.</p>
<h3>Live Sound and PA Systems</h3>
<p>Compression is used in live sound to prevent sudden level spikes, improve clarity, and protect speakers from damage. It ensures speech remains audible in noisy environments.</p>
<h2>Overuse and the Loudness War</h2>
<p>While compression is a powerful tool, excessive use can lead to over-compressed audio, reducing dynamic range and making music sound lifeless. This has led to the loudness war, where mastering engineers push loudness at the cost of clarity. The use of brick wall limiting and aggressive compression can introduce distortion and listener fatigue.</p>
<div style={{marginLeft:'5%', marginRight:'5%', width:'95%', maxWidth:'400px', textAlign:'left'}}>
        <WikiCommonsImage width="100%" bottomText='"Something" by The Beatles - Mastered 4 times ' bottomTextLinkUrl="https://www.thebeatles.com/" wikiCommonsLink="https://commons.wikimedia.org/wiki/File:Cd_loudness_trend-something.gif" imageSource="https://upload.wikimedia.org/wikipedia/commons/a/ab/Cd_loudness_trend-something.gif"  title = "en:User:Aquegg, Public domain, via Wikimedia Commons" />
</div>
<h2 style={{marginTop:'50px'}}>Advanced Compressor Types and Usage</h2>
<h3>Optical Compressors</h3>
<p>These use a light-dependent resistor to control gain, offering smooth and natural compression. Common in vocal processing.</p>
<h3>FET Compressors</h3>
<p>Field-effect transistor (FET) compressors provide fast attack times and are often used for adding punch to drums and vocals.</p>
<h3>VCA Compressors</h3>
<p>Voltage-controlled amplifiers (VCA) allow precise control and are commonly used for bus compression in mixing and mastering.</p>
<h3>Tube Compressors (Variable-Mu)</h3>
<p>These use vacuum tubes to apply gain reduction, producing a warm, musical compression ideal for vocals and master bus processing.</p>
<h2>Best Practices for Using Compression</h2>
<ul class="a">
    <li><strong>Use Compression Sparingly:</strong>Avoid over-compressing; dynamic variation is crucial for natural sound.</li>
    <li><strong>Adjust Attack and Release Properly:</strong> Fast attack times can kill transients, while slow attack times can make the compression ineffective.</li>
    <li><strong>Match Compression to the Material:</strong> Vocals require different settings than drums or bass.</li>
    <li><strong>Combine Multiple Compression Techniques:</strong> Use a mix of parallel, multiband, and serial compression where needed.</li>
    <li><strong>Use Sidechaining for Clarity:</strong> Especially useful for balancing bass and kick drum.</li>
    <li><strong>Always Compare with Bypass:</strong> Regularly A/B test to ensure compression enhances rather than degrades the sound.</li>
</ul>
<p>
Audio compression is an essential technique for achieving a polished, professional sound in music production, broadcasting, live sound, and speech processing. By understanding its principles, types, and applications, audio engineers and producers can harness compression effectively while avoiding common pitfalls. When used properly, compression can enhance clarity, balance dynamics, and create engaging mixes that stand out without sacrificing musicality.
</p>
<br />
    <br />
    <br />
        <p style={{textAlign:'center'}}>* We are not affiliated with any of the brands or artists listed on this site *</p>
      
      </div>
        </>

    )
}

